import React, { useRef, useState } from 'react';
import { Upload, Loader2, X, FileText, FolderOpen } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import MediaLibraryPicker from './MediaLibraryPicker';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.txt,.csv,.zip';

/**
 * Uploads a document (PDF / Office / archive) to /api/upload-document and
 * returns its public URL + original filename via onChange({ url, name }).
 * Pass an empty url to clear.
 */
export default function DocumentUploader({ value, fileName, onChange, testid = 'doc-uploader' }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(''); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      const { data } = await api.post('/upload-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange({ url: data.url, name: data.original_filename || f.name });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error subiendo archivo');
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = '';
    }
  }

  return (
    <div>
      <input ref={ref} type="file" accept={ACCEPT} onChange={onFile} className="hidden"
        data-testid={`${testid}-input`} />
      {value ? (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5">
          <FileText size={14} className="text-emerald-600 flex-shrink-0" />
          <span className="text-xs text-emerald-800 truncate flex-1" title={fileName || value}>{fileName || 'Archivo adjunto'}</span>
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
            className="text-[10px] font-medium text-emerald-700 hover:underline disabled:opacity-50">Reemplazar</button>
          <button type="button" onClick={() => setShowPicker(true)} disabled={uploading}
            data-testid={`${testid}-library-btn`}
            className="text-[10px] font-medium text-cmic-700 hover:underline disabled:opacity-50">Biblioteca</button>
          <button type="button" onClick={() => onChange({ url: '', name: '' })}
            data-testid={`${testid}-clear`} title="Quitar" className="text-emerald-600 hover:text-rose-600">
            <X size={13} />
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
            data-testid={`${testid}-btn`}
            className="inline-flex items-center gap-2 bg-white border border-zinc-300 text-zinc-700 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-zinc-50 disabled:opacity-60">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? 'Subiendo…' : 'Subir archivo'}
          </button>
          <button type="button" onClick={() => setShowPicker(true)} disabled={uploading}
            data-testid={`${testid}-library-btn`}
            className="inline-flex items-center gap-2 bg-white border border-cmic-200 text-cmic-700 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-cmic-50 disabled:opacity-60">
            <FolderOpen size={13} /> Elegir de la biblioteca
          </button>
        </div>
      )}
      {error && <div className="text-xs text-rose-700 mt-1">{error}</div>}

      {showPicker && (
        <MediaLibraryPicker
          filterType={null}
          title="Elegir archivo de la biblioteca"
          onClose={() => setShowPicker(false)}
          onSelect={({ url, name }) => { onChange({ url, name }); setShowPicker(false); }}
        />
      )}
    </div>
  );
}
